import { createContext, useContext } from 'react';

import type {
	MenuControlContext as ContextProps,
	MenuControlContextV2Props,
	MenuStateContextProps,
} from './types';

/**
 * Context for managing section expansion state in the universal create menu
 */
export interface SectionExpansionContextProps {
	sectionExpansionState: Record<number, boolean>;
	setSectionExpansionState: (sectionIndex: number, expanded: boolean) => void;
}

const defaultSectionExpansionContext: SectionExpansionContextProps = {
	sectionExpansionState: {},
	setSectionExpansionState: function (): void {},
};

export const SectionExpansionContext = createContext<SectionExpansionContextProps>(
	defaultSectionExpansionContext,
);


export function useSectionExpansionContext() {
	return useContext(SectionExpansionContext);
}

const defaultFormControls: ContextProps = {
	load: function (): void {},
	reset: function (): void {},
	close: function (): void {},
	mixedItems: [],
	product: 'unknown',
	subProduct: undefined,
	suggestionConfig: {
		enabled: false,
	},
	isNav4Enabled: true,
	crossAppCreationConfig: {
		enabled: false,
	},
	sections: [],
};

/**
 * @deprecated Use MenuControlContextV2 and ItemsContext instead.
 * MenuControlContextV2 provides control methods (load, reset, close) and
 * ItemsContext provides state/data properties.
 */
export const MenuControlContext = createContext<ContextProps>(defaultFormControls);

/**
 * @deprecated Use useMenuControlV2() and useMenuStateContext() instead.
 */
export function useMenuControl() {
	return useContext(MenuControlContext);
}

const defaultFormControlsV2: MenuControlContextV2Props = {
	load: function (): void {},
	reset: function (): void {},
	close: function (): void {},
};

/**
 * Menu Control Context - responsible for controlling the menu, back, cancel and reset to menu list
 */
export const MenuControlContextV2 = createContext<MenuControlContextV2Props>(defaultFormControlsV2);

export function useMenuControlV2() {
	return useContext(MenuControlContextV2);
}

/**
 * Menu State Context - responsible for the state of the menu,
 * Including items/sections, product, subProduct, suggestionConfig and other configs.
 */
export const MenuStateContext = createContext<MenuStateContextProps>({
	isV2Mode: false,
	items: [],
	product: 'unknown',
	subProduct: undefined,
	crossAppCreationConfig: {
		enabled: false,
	},
	sections: [],
	suggestionConfig: {
		enabled: false,
	},
	isNav4Enabled: true,
	preventClose: undefined,
	controlledOpen: undefined,
});

export function useMenuStateContext() {
	return useContext(MenuStateContext);
}